/**
 * Message Bubble Component
 * Displays individual chat messages with role-based styling
 */

import React from 'react';
import { ChatMessage } from '@/store/chat';
import { formatDistance } from 'date-fns';

interface MessageBubbleProps {
  message: ChatMessage;
  isOwnMessage: boolean;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwnMessage }) => {
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'buyer':
        return 'You';
      case 'vendor':
        return 'Vendor';
      case 'rider':
        return 'Rider';
      case 'ops':
        return 'Support';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'buyer':
        return 'bg-green-600 text-white';
      case 'vendor':
        return 'bg-blue-100 text-blue-900';
      case 'rider':
        return 'bg-purple-100 text-purple-900';
      case 'ops':
        return 'bg-gray-100 text-gray-900';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };

  const timeAgo = formatDistance(new Date(message.created_at), new Date(), { addSuffix: true });

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-gray-600">
            {getRoleLabel(message.sender_role)}
          </span>
          <span className="text-xs text-gray-400">{timeAgo}</span>
        </div>
        <div
          className={`rounded-lg px-4 py-2 ${
            isOwnMessage ? getRoleColor('buyer') : getRoleColor(message.sender_role)
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
          
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.attachments.map((attachment, idx) => (
                <div key={idx} className="text-xs opacity-75">
                  📎 {attachment.name || 'Attachment'}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

